import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { RootState, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { useAppDispatch } from '../../../state/state';

interface PricingData {
  deductionPctAboveWM?: number;
  nonUpiEndChargePct?: number;
  upiEndChargePct?: number;
  watermarkPrice?: number;
  watermarkValue?: number;
}

export default function CustomPlanPriceModal() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((state: RootState) => state.ModalSlice.CustomPlanPriceModalOpen);
  const uid = useAppSelector((state: RootState) => state.userSliceV2?.data?.uid);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricingData, setPricingData] = useState<{
    Events?: PricingData;
    Subscriptions?: PricingData;
  }>({});

  function handleClose() {
    dispatch(closeModal("CustomPlanPriceModalOpen"));
  }

  useEffect(() => {
    const fetchPricing = async () => {
      setLoading(true);
      try {
        if (!uid) {
          setError('User not authenticated. Please log in to access pricing data.');
          return;
        }

        const spDocRef = doc(db, 'ServiceProviders', uid);
        const docSnap = await getDoc(spDocRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.pricingTable) {
            setPricingData({
              Subscriptions: data.pricingTable.Subscriptions,
              Events: data.pricingTable.Events,
            });
          }
        } else {
          setError('No pricing data found');
        }
      } catch (err) {
        console.error('Error fetching pricing data:', err);
        setError('Failed to load pricing data');
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [uid]);

  const renderPricingTable = () => {
    return (
      <div className="w-full overflow-hidden rounded-lg shadow-sm">
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="px-6 py-3 bg-gray-100 text-left text-sm font-semibold text-gray-700 font-inter">
                Services
              </th>
              <th className="px-6 py-3 bg-gray-100 text-center text-sm font-semibold text-gray-700 font-inter">
                Transaction Fee<br/>(in % above WM)
              </th>
              <th className="px-6 py-3 bg-gray-100 text-center text-sm font-semibold text-gray-700 font-inter">
                Transaction Fee<br/>(in Rs below WM)
              </th>
              <th className="px-6 py-3 bg-gray-100 text-center text-sm font-semibold text-gray-700 font-inter">
                Watermark Value<br/>(in Rs)
              </th>
            </tr>
          </thead>
          <tbody>
            {['Subscriptions', 'Events'].map((service) => {
              const data = pricingData[service as keyof typeof pricingData];
              return (
                <tr key={service} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 font-inter">
                    {service}
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 font-inter">
                    {data?.deductionPctAboveWM !== undefined ? `${data.deductionPctAboveWM}%` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 font-inter">
                    {data?.watermarkPrice !== undefined ? `₹${data.watermarkPrice}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 font-inter">
                    {data?.watermarkValue !== undefined ? `₹${data.watermarkValue}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* <div className="text-right pr-6 py-2 text-xs italic text-gray-500 font-inter">
          *Fees are inclusive of GST
        </div> */}
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 w-[800px] max-w-[90vw] font-inter relative"> {/* Increased width to 800px */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 font-inter">
            Custom Plan Pricing
          </h2>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
          </div>
        ) : (
          <>
            {renderPricingTable()}
            
            {!pricingData.Events && !pricingData.Subscriptions && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded relative my-4">
                No custom pricing data found
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}